import * as P from '@/constants/paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { Address } from '@viome/viome-base/src/usecase/Register/Register.model';
import { useAddressValidationMutation } from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useToast } from '../use-toast';
import useRegisterStore, {
  RegistrationTrack,
  PhoneCode,
} from '../useRegistrationStore';

const defaultSchema = z.object({
  country: z.string().min(1, { message: 'Country is required' }),
  streetAddress: z.string().min(1, { message: 'street address is required' }),
  apartment: z.string().optional(),
  state: z.string().min(1, { message: 'state is required' }),
  city: z.string().min(1, { message: 'city is required' }),
  zipCode: z.string().min(1, { message: 'zip is required' }),
  phoneCode: z.string().min(1, { message: 'required' }),
  phoneNumber: z.string().min(1, { message: 'phone is required' }),
  allowSms: z.boolean().optional(),
});

const transformedSchema = defaultSchema.transform((val) => ({
  ...val,
  phoneCode: JSON.parse(val.phoneCode) as PhoneCode,
}));

type ContactInformation = z.infer<typeof transformedSchema>;

const useContactInfo = () => {
  const navigate = useNavigate();
  const {
    country,
    street,
    city,
    state,
    zip,
    phone,
    apartment,
    phoneCode,
    offers,
    setAddressDetails,
    setShippable,
    setOffers,
    setRestrictedRegistrationSteps,
    setAddressType,
  } = useRegisterStore();
  const { toast } = useToast();

  const {
    isPending: isPendingAddressValidation,
    error: errorAddressValidation,
    mutate: mutateAddressValidation,
  } = useAddressValidationMutation();

  const checkingForStringOrNumber = (inputPhoneCode: string) => {
    const isPhoneCode = inputPhoneCode.includes('+');
    return isPhoneCode ? true : false;
  }

  const form: UseFormReturn<any> = useForm<z.infer<typeof defaultSchema>>({
    mode: 'onSubmit',
    resolver: zodResolver(transformedSchema),
    defaultValues: {
      country: country,
      streetAddress: street,
      city: city,
      state: state,
      zipCode: zip,
      phoneCode: checkingForStringOrNumber(phoneCode.countryPhoneCode)
        ? JSON.stringify({
            countryPhoneCode: phoneCode.countryPhoneCode,
            countryCode: phoneCode.countryCode,
          })
        : '',
      phoneNumber: phone && phone.startsWith('+1') ? phone.slice(2) : phone,
      apartment: apartment,
      allowSms: offers,
    },
  });

  const onSubmit = (input: ContactInformation) => {
    setAddressType(undefined);
    setAddressDetails({
      country: input.country,
      street: input.streetAddress,
      apartment: input.apartment ?? '',
      state: input.state,
      zip: input.zipCode,
      phone: input.phoneNumber,
      city: input.city,
      phoneCode: input.phoneCode,
    });
    setOffers(input.allowSms ?? false);
    const apiData = {
      country: input.country,
      addressLine1: input.streetAddress,
      addressLine2: input.apartment ?? null,
      zip: input.zipCode,
      city: input.city,
      state: input.state,
    };

    mutateAddressValidation(apiData, {
      onSuccess: (data, postData) => {
        setShippable(data?.shippable);
        const hasSuggestion = !!data?.formattedAddress;
        const addressData: {
          formattedAddress: string | null;
          address: Address | null;
          selected: boolean;
          type: 'input' | 'suggestion';
        }[] = [
          {
            formattedAddress: null,
            address: postData,
            selected: !hasSuggestion,
            type: 'input',
          },
        ];

        if (hasSuggestion) {
          addressData.push({
            formattedAddress: data.formattedAddress,
            address: data?.address,
            selected: true,
            type: 'suggestion',
          });
        }
        Promise.all([
          setRestrictedRegistrationSteps([
            RegistrationTrack.RegistrationConfirmation,
            RegistrationTrack.TermsOfService,
            RegistrationTrack.ConsentForm,
          ]),
          navigate(P.REGISTRATION_ADDRESS_CONFIRMATION, {
            state: { addressData: addressData },
          }),
        ]);
      },
      onError: (error) => {
        toast({
          variant: 'destructive',
          description: error.message,
        });
      },
    });
  };

  return {
    form,
    onSubmit,
    errorAddressValidation,
    isPendingAddressValidation,
  };
};

export default useContactInfo;
